import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import { fetchMenu } from "@/services/menuService";
import { capitalizeText, getImageUrl, showAds } from "@/utilities/menu";
import Footer from "@/components/layout/Footer";
import { useQRCode } from "next-qrcode";
import getStripe from "@/lib/get-stripejs";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouter } from "next/router";

export async function getServerSideProps(
  context: any
): Promise<{ props: PageProps }> {
  const { menu } = context.query;

  let menuData = null;

  if (menu) {
    const data = await fetchMenu(menu);
    if (data) {
      menuData = data;
    } else {
      return {
        notFound: true,
      } as any;
    }
  } else {
    return {
      notFound: true,
    } as any;
  }
  menuData.language = (menuData?.languages?.[0] || "en").split("_")[0];

  return {
    props: {
      menu: menuData,
    },
  };
}

interface PageProps {
  menu: any | null;
}

const CheckoutPage: React.FC<PageProps> = ({ menu }) => {
  const router = useRouter();
  const { translate } = useTranslation(
    ["ar", "fr"].includes(menu.language) ? menu.language : "en"
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [products, setProducts] = useState<any[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [showQrCode, setShowQrCode] = useState<boolean>(false);
  const [order, setOrder] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [split, setSplit] = useState(1);
  const [useCustomTotalPrice, setUseCustomTotalPrice] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState(0);

  const [customTotalPrice, setCustomTotalPrice] = useState<number>(0);

  const [showCustomSplitPopup, setShowCustomSplitPopup] = useState(false);
  const [customSplitPrice, setCustomSplitPrice] = useState(0);
  const [customSplitPriceError, setCustomSplitPriceError] = useState("");

  const [tipPercentage, setTipPercentage] = useState(0);
  const [useCustomTipAmount, setUseCustomTipAmount] = useState(false);
  const [tipsModal, setTipsModal] = useState(false);
  const [table, setTable] = useState<string>("");

  const { Canvas } = useQRCode();

  useEffect(() => {
    const res = readSessionCart(products, totalPrice);
    setProducts(res.products);
    setTotalPrice(res.totalPrice);
    getOrder();
  }, []);

  const menuName = useMemo(
    () => capitalizeText(menu?.siteName?.replace(/-/g, " ")),
    [menu]
  );

  const checkOrderExpiration = (order: any) => {
    if (order && order.createdAt) {
      const createdAt = new Date(order.createdAt).getTime();
      const currentTime = new Date().getTime();
      const timeDifference = currentTime - createdAt;
      const fiveHoursInMilliseconds = 5 * 60 * 60 * 1000;

      if (timeDifference > fiveHoursInMilliseconds) {
        return true;
      }
    }
    return false;
  };

  const getOrder = () => {
    const sessionOrder = localStorage.getItem(`order-${menu.siteName}`);
    const order = sessionOrder ? JSON.parse(sessionOrder) : undefined;
    if (order && order.closed) {
      localStorage.removeItem(`order-${menu.siteName}`);
      localStorage.removeItem(`cart-${menu.siteName}`);
      router.push(`/menus/${menu.siteName}/`);
      return;
    }
    if (order && order.orderId) {
      setOrder(order);
    }
    if (checkOrderExpiration(order)) {
      localStorage.removeItem(`order-${menu.siteName}`);
      localStorage.removeItem(`cart-${menu.siteName}`);
      window.location.reload();
      return;
    }

    // getting table number
    const urlParams = new URLSearchParams(window.location.search);
    const table = urlParams.get("table");
    if (table && table.length) {
      setTable(table);
    }
  };

  const readSessionCart = (products: any, totalPrice: any) => {
    const cartItem = localStorage.getItem(`cart-${menu.siteName}`);
    products = cartItem ? JSON.parse(cartItem) : [];
    products = products.map((prod: any) => {
      prod.show = false;
      return prod;
    });
    totalPrice = products.reduce(
      (prev: any, prod: any) => prev + prod.quantity * prod.totalPrice,
      0
    );
    return { products, totalPrice };
  };

  const showProduct = (product: any) => {
    product.show = true;
  };

  const hideProduct = (product: any) => {
    const res = readSessionCart(products, totalPrice);
    setProducts(res.products);
    setTotalPrice(res.totalPrice);
  };

  const addOne = (product: any) => {
    product.quantity =
      product.quantity === 100 ? product.quantity : product.quantity + 1;
  };

  const subtractOne = (product: any) => {
    product.quantity =
      product.quantity === 1 ? product.quantity : product.quantity - 1;
  };

  const removeItem = (product: any) => {
    const newProducts = products.filter((p) => p !== product);
    setProducts(newProducts);
    localStorage.setItem(`cart-${menu.siteName}`, JSON.stringify(newProducts));
    updateCartStorage(newProducts);
  };

  const updateCartStorage = (products: any) => {
    const totalPrice = products.reduce(
      (prev: any, prod: any) => prev + prod.quantity * prod.onlinePrice,
      0
    );
    setTotalPrice(totalPrice);
    localStorage.setItem(`cart-${menu.siteName}`, JSON.stringify(products));
  };

  const selectOption = (product: any, variant: any, option: any) => {
    variant.options = variant.options.map((opt: any) => {
      opt.selected = opt === option;
      return opt;
    });
    const variantIndex = product.variantTypes.indexOf(variant);
    product[`selectedVariant${variantIndex + 1}`] = option.name;
    let selectedVariant = product.selectedVariant1;
    if (product.has2variant) {
      selectedVariant += " / " + product.selectedVariant2;
    }
    if (product.has3variant) {
      selectedVariant += " / " + product.selectedVariant3;
    }
    product.selectedVariant = selectedVariant;
    const variantFinal = product.variants.find(
      (v: any) => v.name === selectedVariant
    );
    product.onlinePrice = variantFinal.price;
  };

  const getStripeCurrency = (currency: string) => {
    switch (currency) {
      case "€":
        return "eur";
      case "C$":
        return "cad";
      case "MAD":
        return "mad";
      case "A$":
        return "aud";
      case "£":
        return "gbp";
      default:
        return "usd";
    }
  };

  const createOrUpdateOrder = () => {
    const storageOrder = localStorage.getItem(`order-${menu.siteName}`);
    const order = storageOrder ? JSON.parse(storageOrder) : undefined;
    return new Promise((resolve, reject) => {
      if (order) {
        if (JSON.stringify(order.products) !== JSON.stringify(products)) {
          order.products = products;
          fetch(`${process.env.NEXT_PUBLIC_MENU_API}/update-order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(order),
          })
            .then((resp) => resp.json())
            .then((resp) => {
              if (resp && resp.errorCode) {
                const code = resp.errorCode;
                setErrorMessage(resp.message);
                switch (code) {
                  case "PRODUCT_DELETED":
                    localStorage.removeItem(`cart-${menu.siteName}`);
                    const res0 = readSessionCart(products, totalPrice);
                    setProducts(res0.products);
                    setTotalPrice(res0.totalPrice);
                    break;
                  case "PRODUCT_PRICE_CHANGED":
                    localStorage.removeItem(`cart-${menu.siteName}`);
                    const res1 = readSessionCart(products, totalPrice);
                    setProducts(res1.products);
                    setTotalPrice(res1.totalPrice);
                    break;
                  case "ORDER_NOT_FOUND":
                    localStorage.removeItem(`order-${menu.siteName}`);
                    localStorage.removeItem(`cart-${menu.siteName}`);
                    break;
                  case "ORDER_CLOSED":
                    localStorage.removeItem(`order-${menu.siteName}`);
                    localStorage.removeItem(`cart-${menu.siteName}`);
                    break;
                  default:
                    break;
                }
              }
              return resp;
            })
            .then((order) => resolve(order))
            .catch((error) => reject(error));
        } else {
          resolve(order);
        }
      } else {
        // getting table number
        const urlParams = new URLSearchParams(window.location.search);
        const table = urlParams.get("table");
        // creating new order
        const newOrder = {} as any;
        newOrder["products"] = products;
        newOrder["orderType"] = "customer";
        newOrder["tableNumber"] = table && table.length ? table : "0";
        newOrder["siteName"] = menu.siteName;
        newOrder["currency"] = getStripeCurrency(menu.currency);
        // save new order
        fetch(
          `${process.env.NEXT_PUBLIC_MENU_API}/create-order?siteName=${menu.siteName}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newOrder),
          }
        )
          .then((resp) => resp.json())
          .then((order) => resolve(order))
          .catch((error) => reject(error));
      }
    });
  };

  const showQrCodeOrder = () => {
    if (loading || totalPrice === 0) {
      return;
    }
    if (!showQrCode) {
      setShowQrCode(true);
      setLoading(true);

      createOrUpdateOrder()
        .then((data: any) => {
          if (!data) {
            return;
          }
          if (data.errorCode) {
            const code = data.errorCode;
            const message = data.message;
            if (!message) {
              setShowQrCode(false);
              return;
            }
            if (
              code === "PRODUCT_DELETED" ||
              code === "PRODUCT_PRICE_CHANGED"
            ) {
              localStorage.removeItem(`cart-${menu.siteName}`);
              readSessionCart(products, totalPrice);
            }
            setErrorMessage(message);
            setShowQrCode(false);
            return;
          }

          if (data && data.orderId) {
            setOrder(data);
            localStorage.setItem(
              `order-${menu.siteName}`,
              JSON.stringify(data)
            );
          }
        })
        .catch((error) => {
          console.error(error);
          setShowQrCode(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setShowQrCode(false);
    }
  };

  const startCheckout = () => {
    setLoading(true);
    createOrUpdateOrder()
      .then((order: any) => {
        setLoading(true);
        setOrder(order);
        if (!order.orderId) {
          alert("Order not created");

          return;
        }
        if (order.closed) {
          alert("Order Fully Paid");

          return;
        }
        const checkoutSession = {} as any;
        checkoutSession["orderId"] = order.orderId;
        checkoutSession["items"] = useCustomTotalPrice
          ? customTotalPrice
          : totalPrice / (split || 1);
        checkoutSession["tips"] = useCustomTipAmount
          ? customTipAmount
          : ((useCustomTotalPrice
              ? customTotalPrice
              : totalPrice / (split || 1)) *
              (tipPercentage || 0)) /
            100;
        fetch(
          `${process.env.NEXT_PUBLIC_MENU_API}/stripe/create-checkout?siteName=${menu.siteName}`,
          {
            mode: "cors",
            method: "post",
            body: JSON.stringify(checkoutSession),
          }
        )
          .then((resp) => {
            if (resp.status === 200 || resp.status === 400) {
              const dataJSON = resp.json();
              return dataJSON;
            } else {
              return Promise.reject("server");
            }
          })
          .then(async (data) => {
            if (!data) {
              return;
            }
            if (data.errorCode) {
              const code = data.errorCode;
              const message = data.message;
              if (!message) {
                return;
              }
              if (code === "NO_PAYMENT_SERVICE") {
                setErrorMessage(message);
              } else {
                console.error("ERROR MESSAGE", message);
              }
              return;
            }
            if (data.session) {
              // stripe checkout
              const session = data.session;
              const stripeAccount = data.stripeAccount;

              const stripe = await getStripe({
                stripeAccount,
              });
              localStorage.setItem(`OrderId-${menu.siteName}`, data.orderId);
              localStorage.setItem(
                `OrderDate-${menu.siteName}`,
                new Date().toISOString()
              );
              if (stripe) {
                stripe
                  .redirectToCheckout({
                    sessionId: session.id,
                  })
                  .then((result: any) => {})
                  .catch((err: any) => {});
              }
            }
            if (data.token) {
              const form = document.createElement("form");
              document.body.appendChild(form);
              form.method = "POST";
              form.action = "https://test.authorize.net/payment/payment";
              const inputToken = document.createElement("input");
              inputToken.name = "token";
              inputToken.value = data.token;
              inputToken.type = "hidden";
              form.appendChild(inputToken);
              form.submit();
            }
          })
          .catch((err) => {
            console.log(err);
          })
          .finally(() => {
            // setLoading(false);
          });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCustomSplitClick = () => {
    setShowCustomSplitPopup(true);
  };

  const handleCustomSplitPriceChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(e.target.value);
    setCustomSplitPrice(value);

    if (isNaN(value)) {
      setCustomSplitPriceError("Please enter a valid number.");
    } else if (value <= 0) {
      setCustomSplitPriceError("Price must be greater than zero.");
    } else if (value > totalPrice) {
      setCustomSplitPriceError("Price cannot exceed the total price.");
    } else {
      setCustomSplitPriceError("");
    }
  };

  const handleSubmitCustomSplitPrice = () => {
    if (customSplitPrice && customSplitPrice <= totalPrice) {
      setSplit(0);
      setUseCustomTotalPrice(true);
      setCustomTotalPrice(customSplitPrice);
      setShowCustomSplitPopup(false);
    }
  };

  const shouldShowAds = showAds(menu);

  return (
    <>
      <Head>
        <title>Checkout Page</title>
        <link rel="icon" href="/favicon.ico" />
        <meta name="description" content="Checkout Page" />
        <meta property="og:title" content="Checkout Page" />
        <meta property="og:description" content="Checkout Page" />
      </Head>
      <div>
        <div className="flex flex-col min-h-screen bg-white font-inter  overflow-hidden mt-[-20px] relative">
          <section>
            <div className="h-full">
              {/* Container */}
              <div className="container mx-auto h-full max-w-[500px] p-0">
                {/* Close Button */}
                <div className="relative h-[45px] mt-3">
                  {order && (
                    <a
                      href={`/menus/${menu.siteName}/`}
                      className="absolute top-[5px] left-[12px] right-auto h-[40px] text-black z-10 flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="36"
                        height="36"
                        viewBox="7 0 36 36"
                        fill="none"
                        style={{ minWidth: "36px" }}
                      >
                        <path
                          d="M15.0823 10.4175L15.0823 16.4175L28.4623 16.4175L28.5073 19.4325L15.0823 19.4325L15.0823 25.4175L7.58232 17.9175L15.0823 10.4175Z"
                          fill="black"
                        />
                      </svg>
                      <span className="-ml-2">{translate("back")}</span>
                    </a>
                  )}
                </div>
                {errorMessage && (
                  <div className="fixed inset-0 flex items-center justify-center z-50">
                    <div
                      className="bg-black opacity-50 absolute inset-0"
                      onClick={() => {
                        setErrorMessage("");
                      }}
                    ></div>
                    <div
                      id="toast-warning"
                      className="flex items-center w-full max-w-xs p-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800 z-50"
                      role="alert"
                    >
                      <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-orange-500 bg-orange-100 rounded-lg dark:bg-orange-700 dark:text-orange-200">
                        <svg
                          className="w-5 h-5"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z" />
                        </svg>
                        <span className="sr-only">Warning icon</span>
                      </div>
                      <div className="ms-3 text-sm font-normal">
                        {errorMessage}
                      </div>
                      <button
                        type="button"
                        className="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
                        data-dismiss-target="#toast-warning"
                        aria-label="Close"
                        onClick={() => {
                          setErrorMessage("");
                        }}
                      >
                        <span className="sr-only">Close</span>
                        <svg
                          className="w-3 h-3"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 14 14"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {/* Main Content */}
                <div className="mx-3 mt-5 border border-color-[#d1d1d1] pt-1 pb-5 shadow-md font-roboto font-sans font-arial text-[13px]">
                  <div className="text-left">
                    <img
                      src="https://cdn.dopweb.com/qrmenu/QR-menu-logo.png"
                      className="max-w-full w-[115px] pl-1"
                    />
                  </div>

                  <div className="flex justify-between border-b border-black p-[0 15px] mb-[15px] px-3 my-3">
                    <span className="uppercase">
                      {translate("table")} : {table || "-"}
                    </span>
                    <span>
                      {order.orderId
                        ? `${translate("order_0")} #${order.orderId}`
                        : ""}
                    </span>
                  </div>

                  {/* Product List */}
                  <div className="px-3">
                    {products?.map((product: any, index: number) => {
                      return (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                        >
                          <span
                            onClick={() => showProduct(product)}
                            className="flex justify-start items-start w-full"
                          >
                            {product.title + " (x" + product.quantity + ")"}
                          </span>
                          <span
                            onClick={() => showProduct(product)}
                            className="flex justify-end items-start"
                          >
                            {(product.quantity * product.totalPrice).toFixed(2)}
                          </span>
                          <div className="flex justify-start items-center ml-10 mt-2 mb-2">
                            <button
                              className="text-black bg-transparent"
                              onClick={() => removeItem(product)}
                            >
                              X
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-between p-[0 15px] my-[30px px-3 mt-8">
                    <span>{translate("amount_due")}</span>
                    <span>
                      {totalPrice.toFixed(2)}
                      {menu.currency}
                    </span>
                  </div>
                </div>

                {totalPrice ? (
                  <div className="p-4">
                    <div className="mt-5 border border-color-[#d1d1d1] shadow">
                      <button
                        className="p-3 w-full text-[13px] font-semi-bold tracking-wider uppercase"
                        onClick={showQrCodeOrder}
                        disabled={totalPrice === 0}
                      >
                        {split > 1 && !useCustomTotalPrice
                          ? `${translate("split_the_bill_in")} ` + split
                          : translate("share_your_order")}
                      </button>

                      <div
                        className={`text-center overflow-hidden transition-height ease-in-out duration-300 ${
                          showQrCode ? "h-[750px]" : "h-0"
                        }`}
                      >
                        {order?.orderId ? (
                          <div className="flex justify-center">
                            <Canvas
                              text={`https://${process.env.NEXT_PUBLIC_URL_DOMAIN}/order/?orderId=${order.orderId}`}
                              options={{
                                errorCorrectionLevel: "M",
                                margin: 2,
                                scale: 1,
                                width: 270,
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="flex justify-center items-center min-h-[270px]"
                            role="status"
                          >
                            <svg
                              aria-hidden="true"
                              className="w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-black"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill"
                              />
                            </svg>
                            <span className="sr-only">Loading...</span>
                          </div>
                        )}

                        <span className="text-[14px] mx-auto inline-block">
                          {order.orderId}
                        </span>
                        <div>
                          <button
                            className="split-btn"
                            onClick={() => {
                              setSplit(1);
                              setUseCustomTotalPrice(false);
                              setShowQrCode(false);
                            }}
                            style={
                              split === 1 ? { border: "1px solid #000" } : {}
                            }
                          >
                            <span>{translate("pay_full")}</span>
                            <span>
                              {totalPrice.toFixed(2) + (menu.currency || "$")}
                            </span>
                          </button>
                          <button
                            className="split-btn"
                            onClick={() => {
                              setSplit(2);
                              setUseCustomTotalPrice(false);
                              setShowQrCode(false);
                            }}
                            style={
                              split === 2 ? { border: "1px solid #000" } : {}
                            }
                          >
                            <span>{translate("split_in")} 2</span>
                            <span>
                              {totalPrice.toFixed(2) +
                                (menu.currency || "$") +
                                " /2"}
                            </span>
                            <span>
                              {(totalPrice / 2).toFixed(2) +
                                (menu.currency || "$")}
                            </span>
                          </button>
                          <button
                            className="split-btn"
                            onClick={() => {
                              setSplit(3);
                              setUseCustomTotalPrice(false);
                              setShowQrCode(false);
                            }}
                            style={
                              split === 3 ? { border: "1px solid #000" } : {}
                            }
                          >
                            <span>{translate("split_in")} 3</span>
                            <span>
                              {totalPrice.toFixed(2) +
                                (menu.currency || "$") +
                                " /3"}
                            </span>
                            <span>
                              {(totalPrice / 3).toFixed(2) +
                                (menu.currency || "$")}
                            </span>
                          </button>
                          <button
                            className="split-btn"
                            onClick={() => {
                              setSplit(4);
                              setUseCustomTotalPrice(false);
                              setShowQrCode(false);
                            }}
                            style={
                              split === 4 ? { border: "1px solid #000" } : {}
                            }
                          >
                            <span>{translate("split_in")} 4</span>
                            <span>
                              {totalPrice.toFixed(2) +
                                (menu.currency || "$") +
                                " /4"}
                            </span>
                            <span>
                              {(totalPrice / 4).toFixed(2) +
                                (menu.currency || "$")}
                            </span>
                          </button>
                          <button
                            className="split-btn"
                            onClick={() => {
                              setSplit(5);
                              setUseCustomTotalPrice(false);
                              setShowQrCode(false);
                            }}
                            style={
                              split === 5 ? { border: "1px solid #000" } : {}
                            }
                          >
                            <span>{translate("split_in")} 5</span>
                            <span>
                              {totalPrice.toFixed(2) +
                                (menu.currency || "$") +
                                " /5"}
                            </span>
                            <span>
                              {(totalPrice / 5).toFixed(2) +
                                (menu.currency || "$")}
                            </span>
                          </button>
                          <button
                            className="split-btn"
                            onClick={handleCustomSplitClick}
                            style={
                              customSplitPrice > 0
                                ? { border: "1px solid #000" }
                                : {}
                            }
                          >
                            <span>{translate("custom_split")}</span>
                          </button>
                          {showCustomSplitPopup && (
                            <div className="fixed inset-0 flex items-center justify-center z-50">
                              <div
                                className="bg-black opacity-50 absolute inset-0"
                                onClick={() => setShowCustomSplitPopup(false)}
                              ></div>
                              <div className="bg-white p-6 rounded-lg shadow-lg z-50 w-80">
                                <h3 className="text-center text-lg font-semibold mb-4">
                                  {translate("custom_split_price")}
                                </h3>
                                <input
                                  min={0}
                                  max={totalPrice}
                                  type="number"
                                  className="w-full p-2 border rounded mb-4"
                                  placeholder={translate(
                                    "enter_custom_split_price"
                                  )}
                                  value={customSplitPrice || 0}
                                  onChange={handleCustomSplitPriceChange}
                                />
                                {customSplitPriceError && (
                                  <p className="text-red-500 text-xs mb-2">
                                    {customSplitPriceError}
                                  </p>
                                )}
                                <div className="flex justify-between">
                                  <button
                                    className="px-4 py-2 bg-gray-300 rounded"
                                    onClick={() =>
                                      setShowCustomSplitPopup(false)
                                    }
                                  >
                                    {translate("cancel")}
                                  </button>
                                  <button
                                    className="px-4 py-2 bg-[#22c55e] text-white rounded"
                                    onClick={handleSubmitCustomSplitPrice}
                                  >
                                    {translate("submit")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-center text-[13px] mt-8 px-3 uppercase">
                        {translate("add_tips_for_the_waiter")}
                      </h3>
                      <div className="tips">
                        <div
                          className={`tip-content`}
                          onClick={() => {
                            setTipPercentage(0);
                            setUseCustomTipAmount(false);
                            setShowQrCode(false);
                          }}
                        >
                          <button
                            className={`tip-btn ${
                              tipPercentage === 0 ? "selected" : ""
                            }`}
                          >
                            0
                          </button>
                          <span className="text-[12px]">0.00</span>
                        </div>
                        <div
                          className={`tip-content`}
                          onClick={() => {
                            setTipPercentage(5);
                            setUseCustomTipAmount(false);
                            setShowQrCode(false);
                          }}
                        >
                          <button
                            className={`tip-btn ${
                              tipPercentage === 5 ? "selected" : ""
                            }`}
                          >
                            5%
                          </button>
                          <span className="text-[12px]">
                            {(
                              ((useCustomTotalPrice
                                ? useCustomTipAmount
                                  ? customTipAmount
                                  : customTotalPrice
                                : useCustomTipAmount
                                ? customTipAmount
                                : totalPrice / (split || 1)) *
                                5) /
                              100
                            ).toFixed(2) + (menu.currency || "$")}
                          </span>
                        </div>
                        <div
                          className={`tip-content`}
                          onClick={() => {
                            setTipPercentage(18);
                            setUseCustomTipAmount(false);
                            setShowQrCode(false);
                          }}
                        >
                          <button
                            className={`tip-btn ${
                              tipPercentage === 18 ? "selected" : ""
                            }`}
                          >
                            18%
                          </button>
                          <span className="text-[12px]">
                            {(
                              ((useCustomTotalPrice
                                ? useCustomTipAmount
                                  ? customTipAmount
                                  : customTotalPrice
                                : useCustomTipAmount
                                ? customTipAmount
                                : totalPrice / (split || 1)) *
                                18) /
                              100
                            ).toFixed(2) + (menu.currency || "$")}
                          </span>
                        </div>
                        <div
                          className={`tip-content`}
                          onClick={() => {
                            setTipPercentage(22);
                            setUseCustomTipAmount(false);
                            setShowQrCode(false);
                          }}
                        >
                          <button
                            className={`tip-btn ${
                              tipPercentage === 22 ? "selected" : ""
                            }`}
                          >
                            22%
                          </button>
                          <span className="text-[12px]">
                            {(
                              ((useCustomTotalPrice
                                ? useCustomTipAmount
                                  ? customTipAmount
                                  : customTotalPrice
                                : useCustomTipAmount
                                ? customTipAmount
                                : totalPrice / (split || 1)) *
                                22) /
                              100
                            ).toFixed(2) + (menu.currency || "$")}
                          </span>
                        </div>
                        <div className="tip-content" hidden>
                          <button
                            className={`tip-btn ${
                              useCustomTipAmount ? "selected" : ""
                            }`}
                            onClick={() => {
                              setTipsModal(!tipsModal);
                            }}
                          >
                            ___
                          </button>
                          <span x-text="useCustomTipAmount ? customTipAmount.toFixed(2) + (menu.currency || '${currency.toUpperCase()}') : ''"></span>
                        </div>
                      </div>
                      {tipsModal && (
                        <div className="modal">Tips modal content here</div>
                      )}
                      <div className="flex justify-between px-4 pt-2 pb-5">
                        <span className="text-[15px] font-semibold uppercase">
                          {translate("my_total")}
                        </span>
                        <span className="text-[18px] font-semibold">
                          {(
                            (useCustomTotalPrice
                              ? customTotalPrice
                              : totalPrice / (split || 1)) +
                            (useCustomTipAmount
                              ? customTipAmount
                              : ((useCustomTotalPrice
                                  ? customTotalPrice
                                  : totalPrice / (split || 1)) *
                                  (tipPercentage || 0)) /
                                100)
                          ).toFixed(2) + (menu.currency || "$")}
                        </span>
                      </div>
                      {!order.closed && (
                        <>
                          <button
                            className="pay-now-btn"
                            onClick={startCheckout}
                            disabled={
                              totalPrice === 0 ||
                              loading ||
                              (tipPercentage === undefined &&
                                !useCustomTipAmount)
                            }
                            style={loading ? { backgroundColor: "#ccc" } : {}}
                          >
                            <span>{translate("pay_now")}</span>
                            {loading ? (
                              <svg
                                aria-hidden="true"
                                role="status"
                                className="inline w-4 h-4 ml-2 text-white animate-spin"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                  fill="#E5E7EB"
                                />
                                <path
                                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                  fill="currentColor"
                                />
                              </svg>
                            ) : (
                              ""
                            )}
                          </button>
                          <p
                            style={{
                              textAlign: "center",
                              fontSize: "11px",
                              margin: "0",
                            }}
                          >
                            {translate("clicking_pay_accept")}{" "}
                            <a
                              href="https://qrmenu.com"
                              className="text-blue-500"
                            >
                              {translate("user_conditions")}
                            </a>
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                {order.closed && (
                  <div>
                    <h3
                      className="text-center text-[13px] mt-8 px-3"
                      style={{
                        color: "#5bb953",
                        fontWeight: "600",
                        fontSize: "17px",
                      }}
                    >
                      {translate("order_fully_paid")}
                    </h3>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Bottom Restaurant Info Section */}
          <div className="m-[5%] py-2 bg-white">
            <h2 className="text-[22px] font-bold text-gray-900 leading-[30px]">
              {menuName}
            </h2>
            <span className="text-[15px] text-gray-900 leading-[10px]">
              {menu?.tagLine}
            </span>
          </div>

          {/* Footer Section */}
          <Footer siteName={menu.siteName} loadAds={shouldShowAds} />
        </div>
      </div>
    </>
  );
};
export default CheckoutPage;
